import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useQuotes,
  useQuoteStats,
  useQuoteV3,
  useQuoteV4,
  useQuoteByMongoId,
  useEnsureQuoteMongo,
  useCreateQuote,
  useCreateQuoteForNewCustomer,
  useUpdateQuote,
  useDeleteQuote,
  useDiscardQuote,
} from '../hooks';
import { getQuotes } from '../api';
import { useSalesManagers } from '@/features/accounts/hooks/useSubscriptionManagers';
import {
  parseSalesManagerFilterValue,
} from '@/features/accounts/utils/salesManagerFilters';
import { QuotesFilters, ALL_STATUSES } from '../components/QuotesFilters';
import { QuoteStatsBar } from '../components/QuoteStatsBar';
import { QuotesTable } from '../components/QuotesTable';
import { QuoteFormModal, QuoteDeleteDialog, QuoteDiscardDialog } from '../components/modals';
import { useHasQuotePermission } from '../hooks/useQuotePermission';
import { QUOTE_PERMISSIONS } from '../permissions';
import type {
  Quote,
  QuotesFilters as QuotesFiltersType,
  QuoteStatus,
  CreateQuoteInput,
  UpdateQuoteInput,
} from '../types';

// Default statuses to show (exclude cancelled, rejected, expired by default)
const DEFAULT_STATUSES: QuoteStatus[] = [
  'preparation',
  'sent',
  'waiting-po',
  'approved',
  'inprogress',
  'invoice-ready',
  'partially-invoiced',
  'invoiced',
];

// Derive from the canonical list to stay in sync automatically
const TOTAL_STATUS_COUNT = ALL_STATUSES.length;
const QUOTE_COLUMN_FILTERS_STORAGE_KEY = 'atek-quotes-column-filters';
const ALL_ACCOUNTS_VALUE = 'all';
const POSTGRES_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function parseStoredAccountFilterValue(rawValue: unknown): string {
  if (typeof rawValue !== 'string') return ALL_ACCOUNTS_VALUE;

  const value = rawValue.trim();
  if (!value || value === ALL_ACCOUNTS_VALUE) return ALL_ACCOUNTS_VALUE;

  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) {
      return value;
    }

    return parsed.length === 1 && typeof parsed[0] === 'string' && parsed[0].trim()
      ? parsed[0]
      : ALL_ACCOUNTS_VALUE;
  } catch {
    return value;
  }
}

function loadStoredQuotesFilterState(): {
  columnFilters: Record<string, string>;
  selectedAccountId: string;
} {
  try {
    const rawStored = JSON.parse(
      localStorage.getItem(QUOTE_COLUMN_FILTERS_STORAGE_KEY) || '{}',
    );

    if (!rawStored || typeof rawStored !== 'object' || Array.isArray(rawStored)) {
      return { columnFilters: {}, selectedAccountId: ALL_ACCOUNTS_VALUE };
    }

    const stored = rawStored as Record<string, string>;
    const salesManagerId = parseSalesManagerFilterValue(stored.salesManager);
    const selectedAccountId = parseStoredAccountFilterValue(stored.accountName);
    const { accountName: _accountName, ...restStored } = stored;

    return {
      columnFilters: {
        ...restStored,
        salesManager: salesManagerId || '',
      },
      selectedAccountId,
    };
  } catch {
    return { columnFilters: {}, selectedAccountId: ALL_ACCOUNTS_VALUE };
  }
}

const COLUMN_FIELD_MAP: Record<string, keyof Quote> = {
  quoteNumber: 'quoteNumber',
  accountNumber: 'accountNumber',
  projectName: 'projectName',
  contractualManager: 'contractualManagerName',
  poNumber: 'poNumber',
  contract: 'contractName',
  ticket: 'ticketName',
  projectNumber: 'projectNumber',
  nextStep: 'nextStep',
  subscription: 'subscriptionName',
  billingAddress: 'billingAddress',
  shippingAddress: 'shippingAddress',
  createdAt: 'createdAt',
  expirationDate: 'expirationDate',
  acceptedDate: 'acceptedDate',
  quoteDate: 'createdDate',
  nextStepDeadline: 'nextStepDeadline',
  createdAtFull: 'createdAt',
  status: 'status',
  dealType: 'dealType',
};

const DATE_FILTER_COLUMNS = new Set([
  'createdAt', 'expirationDate', 'acceptedDate', 'quoteDate', 'nextStepDeadline', 'createdAtFull',
]);

const MULTI_SELECT_COLUMNS = new Set(['status', 'dealType']);

const SERVER_SIDE_COLUMNS = new Set(['accountName', 'salesManager', 'poNumber', 'accountNumber']);

function applyClientColumnFilters(
  sourceQuotes: Quote[],
  columnFilters: Record<string, string>,
): Quote[] {
  let result = sourceQuotes;
  const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
  if (activeFilters.length === 0) return result;

  for (const [colKey, value] of activeFilters) {
    if (SERVER_SIDE_COLUMNS.has(colKey)) continue;

    const field = COLUMN_FIELD_MAP[colKey];
    if (!field) continue;

    if (DATE_FILTER_COLUMNS.has(colKey)) {
      const [from, to] = value.split('>>');
      if (from) result = result.filter(q => String(q[field] || '') >= from);
      if (to) result = result.filter(q => String(q[field] || '') <= to + 'T23:59:59');
      continue;
    }

    if (MULTI_SELECT_COLUMNS.has(colKey)) {
      try {
        const selected = JSON.parse(value) as string[];
        if (selected.length) result = result.filter(q => selected.includes(String(q[field] || '')));
      } catch { /* ignore */ }
      continue;
    }

    const search = value.toLowerCase();
    result = result.filter(q => {
      const fieldValue = String(q[field] ?? '').toLowerCase();
      return fieldValue.includes(search);
    });
  }

  return result;
}

export default function QuotesPage() {
  const { t } = useTranslation('quotes');
  const initialFilterState = useMemo(loadStoredQuotesFilterState, []);

  const canCreate = useHasQuotePermission(QUOTE_PERMISSIONS.create);
  const canEdit = useHasQuotePermission(QUOTE_PERMISSIONS.edit);
  const canDelete = useHasQuotePermission(QUOTE_PERMISSIONS.delete);
  const canDiscard = useHasQuotePermission(QUOTE_PERMISSIONS.edit);

  // URL parameters for highlighting and deep linking
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const deepLinkId = searchParams.get('id');

  // Filter state - when highlight param present, search for that quote and show all statuses
  const [selectedStatuses, setSelectedStatuses] = useState<QuoteStatus[]>(
    highlightId ? [] : DEFAULT_STATUSES
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortKey, setSortKey] = useState<QuotesFiltersType['sortKey']>(
    highlightId ? 'quoteNumber' : 'createdAt'
  );
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);
  const [selectedAccountId, setSelectedAccountId] = useState(
    initialFilterState.selectedAccountId,
  );

  // Column filter state (persisted to localStorage)
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    initialFilterState.columnFilters,
  );

  useEffect(() => {
    localStorage.setItem(
      QUOTE_COLUMN_FILTERS_STORAGE_KEY,
      JSON.stringify({
        ...columnFilters,
        accountName:
          selectedAccountId !== ALL_ACCOUNTS_VALUE ? selectedAccountId : '',
      }),
    );
  }, [columnFilters, selectedAccountId]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  const poNumberFilter = columnFilters.poNumber?.trim() || '';
  const accountNumberFilter = columnFilters.accountNumber?.trim() || '';

  // Extract text column filter values for server-side search
  // (date-range values contain ">>" and selects start with "[" — skip those)
  const columnSearchText = useMemo(() => {
    return Object.entries(columnFilters)
      .filter(([key, value]) => {
        if (!value) return false;
        if (key === 'poNumber') return false;
        if (key === 'accountNumber') return false;
        if (key === 'accountName') return false;
        if (key === 'salesManager') return false;
        if (value.includes('>>')) return false;
        if (value.startsWith('[')) return false;
        return true;
      })
      .map(([, value]) => value)
      .join(' ');
  }, [columnFilters]);

  // Debounce column search text for server-side queries
  const [debouncedColumnSearch, setDebouncedColumnSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedColumnSearch(columnSearchText);
      if (columnSearchText) setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [columnSearchText]);

  const [debouncedPoNumber, setDebouncedPoNumber] = useState(poNumberFilter);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPoNumber(poNumberFilter);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [poNumberFilter]);

  // # Compte filter: dedicated server-side param (accounts.accountNumber only),
  // never folded into the global multi-field search — otherwise the server total
  // counts coincidental matches (project name/PO containing the digits) that the
  // table does not display, and the counter disagrees with the table rows.
  const [debouncedAccountNumber, setDebouncedAccountNumber] = useState(accountNumberFilter);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAccountNumber(accountNumberFilter);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [accountNumberFilter]);

  // Modal state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingQuoteId, setEditingQuoteId] = useState<string | null>(null);
  const [editingQuotePgId, setEditingQuotePgId] = useState<string | null>(null);
  const [editingQuoteContext, setEditingQuoteContext] = useState<Quote | null>(null);
  const [duplicateSourcePgId, setDuplicateSourcePgId] = useState<string | null>(null);
  const [deletingQuote, setDeletingQuote] = useState<Quote | null>(null);
  const [discardingQuote, setDiscardingQuote] = useState<Quote | null>(null);
  const ensuringEditPgQuoteIdRef = useRef<string | null>(null);
  // Remembers the PG id of a quote created during the current modal session via
  // "Save" (without closing). Lets a subsequent save UPDATE that quote instead of
  // creating a duplicate. Reset whenever the create/edit modal (re)opens or closes.
  const createdQuotePgIdRef = useRef<string | null>(null);

  // Deep link: open edit modal when ?id= is present
  useEffect(() => {
    if (deepLinkId) {
      if (POSTGRES_UUID_PATTERN.test(deepLinkId)) {
        setEditingQuotePgId(deepLinkId);
      } else {
        setEditingQuoteId(deepLinkId);
      }
      setIsFormOpen(true);
      // Clear the param to avoid re-opening on every render
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('id');
        return next;
      }, { replace: true });
    }
  }, [deepLinkId, setSearchParams]);

  const accountIdIn = useMemo(
    (): string[] | undefined =>
      selectedAccountId && selectedAccountId !== ALL_ACCOUNTS_VALUE
        ? [selectedAccountId]
        : undefined,
    [selectedAccountId],
  );

  const salesManagersQuery = useSalesManagers();
  const salesManagers = salesManagersQuery.data || [];

  const salesManagerId = useMemo(
    () => parseSalesManagerFilterValue(columnFilters.salesManager),
    [columnFilters.salesManager],
  );

  // Build filters object
  const combinedSearch = [highlightId, debouncedColumnSearch].filter(Boolean).join(' ') || undefined;
  const filters: QuotesFiltersType = {
    search: combinedSearch,
    poNumber: debouncedPoNumber || undefined,
    accountNumber: debouncedAccountNumber || undefined,
    accountIdIn,
    salesManagerId,
    status_in: selectedStatuses.length > 0 && selectedStatuses.length < TOTAL_STATUS_COUNT
      ? selectedStatuses
      : undefined,
    sortKey,
    sortDirection,
    pageNumber: page,
    pageLength: pageSize,
  };
  const statsFilters = useMemo(
    (): Partial<QuotesFiltersType> => ({
      search: filters.search,
      poNumber: filters.poNumber,
      accountNumber: filters.accountNumber,
      accountIdIn: filters.accountIdIn,
      salesManagerId: filters.salesManagerId,
      status_in: filters.status_in,
    }),
    [
      filters.search,
      filters.poNumber,
      filters.accountNumber,
      filters.accountIdIn,
      filters.salesManagerId,
      filters.status_in,
    ],
  );

  // Queries
  const quotesQuery = useQuotes(filters);
  const statsQuery = useQuoteStats(statsFilters);

  // Edit form always reads from PostgreSQL (V4), with V3 loaded opportunistically
  // when the linked MongoDB quote still exists.
  const editQuoteV3 = useQuoteV3(editingQuoteId);
  const deepLinkedQuote = useQuoteByMongoId(editingQuotePgId ? null : editingQuoteId);
  const editQuoteV4 = useQuoteV4(editingQuotePgId);
  // Duplicate: load the source quote's full V4 detail so the form can be seeded
  // from it in create mode (a fresh quote, no edit target).
  const duplicateSourceQuery = useQuoteV4(duplicateSourcePgId);

  useEffect(() => {
    if (!editingQuotePgId && deepLinkedQuote.data?.id) {
      setEditingQuotePgId(deepLinkedQuote.data.id);
      setEditingQuoteContext(deepLinkedQuote.data);
    }
  }, [editingQuotePgId, deepLinkedQuote.data]);

  useEffect(() => {
    if (!editingQuoteId && editQuoteV4.data?.mongoQuoteId) {
      setEditingQuoteId(editQuoteV4.data.mongoQuoteId);
    }
  }, [editingQuoteId, editQuoteV4.data?.mongoQuoteId]);

  useEffect(() => {
    if (!editingQuoteId || editingQuotePgId) return;
    if (deepLinkedQuote.isError) {
      toast.error(t('toast.loadQuoteError', 'Failed to load quote for editing'));
      setEditingQuoteId(null);
      setEditingQuotePgId(null);
      setEditingQuoteContext(null);
      setIsFormOpen(false);
    } else if (deepLinkedQuote.isSuccess && !deepLinkedQuote.data) {
      toast.error(t('toast.quoteNotFound', 'Quote not found in database'));
      setEditingQuoteId(null);
      setEditingQuotePgId(null);
      setEditingQuoteContext(null);
      setIsFormOpen(false);
    }
  }, [editingQuoteId, editingQuotePgId, deepLinkedQuote.isError, deepLinkedQuote.isSuccess, deepLinkedQuote.data, t]);

  useEffect(() => {
    if (!editingQuotePgId) return;
    if (editQuoteV4.isError) {
      toast.error(t('toast.loadQuoteError', 'Failed to load quote for editing'));
      setEditingQuoteId(null);
      setEditingQuotePgId(null);
      setEditingQuoteContext(null);
      setIsFormOpen(false);
    } else if (editQuoteV4.isSuccess && !editQuoteV4.data) {
      toast.error(t('toast.quoteNotFound', 'Quote not found in database'));
      setEditingQuoteId(null);
      setEditingQuotePgId(null);
      setEditingQuoteContext(null);
      setIsFormOpen(false);
    }
  }, [editingQuotePgId, editQuoteV4.isError, editQuoteV4.isSuccess, editQuoteV4.data, t]);

  // Mutations
  const createMutation = useCreateQuote();
  const createForNewCustomerMutation = useCreateQuoteForNewCustomer();
  const updateMutation = useUpdateQuote();
  const ensureMongoMutation = useEnsureQuoteMongo();
  const deleteMutation = useDeleteQuote();
  const discardMutation = useDiscardQuote();

  useEffect(() => {
    if (
      !editingQuotePgId
      || editingQuoteId
      || !editQuoteV4.isSuccess
      || !editQuoteV4.data
      || editQuoteV4.data.mongoQuoteId
      || ensuringEditPgQuoteIdRef.current === editingQuotePgId
    ) {
      return;
    }

    ensuringEditPgQuoteIdRef.current = editingQuotePgId;
    ensureMongoMutation.mutateAsync(editingQuotePgId)
      .then((ensured) => {
        setEditingQuoteId(ensured.mongoQuoteId);
      })
      .catch(() => {
        setEditingQuoteId(null);
        setEditingQuotePgId(null);
        setEditingQuoteContext(null);
        setIsFormOpen(false);
      })
      .finally(() => {
        if (ensuringEditPgQuoteIdRef.current === editingQuotePgId) {
          ensuringEditPgQuoteIdRef.current = null;
        }
      });
  }, [editingQuotePgId, editingQuoteId, editQuoteV4.isSuccess, editQuoteV4.data, ensureMongoMutation]);

  const editingInitialCustomerData = useMemo(() => {
    const source = editingQuoteContext;
    const v4Quote = editQuoteV4.data;

    if (!source && !v4Quote) {
      return undefined;
    }

    return {
      accountName: source?.accountName || v4Quote?.accountName || undefined,
      accountNumber: source?.accountNumber || v4Quote?.accountNumber || undefined,
      contractId: source?.contractId || v4Quote?.subscriptionId || undefined,
      salesManagerId: source?.salesManagerId || v4Quote?.salesManagerId || undefined,
      contractualManagerId: source?.contractualManagerId || v4Quote?.contractualManagerId || undefined,
      pgAccountId: source?.accountId || v4Quote?.accountId || undefined,
      lockCustomer: true,
    };
  }, [editingQuoteContext, editQuoteV4.data]);

  const rawQuotes = quotesQuery.data?.quotes || [];
  const statsResult = statsQuery.data;
  const stats = statsResult?.stats || [];
  const isStatsApproximate = statsResult?.isApproximate || false;
  const rawTotal = quotesQuery.data?.total || 0;

  // Client-side column filtering (for columns not yet server-side)
  const filteredQuotes = useMemo(() => {
    return applyClientColumnFilters(rawQuotes, columnFilters);
  }, [rawQuotes, columnFilters]);

  const quotes = filteredQuotes;
  const total = rawTotal;
  // Searchable-select dropdown options
  const searchableSelectOptions = useMemo(() => ({
    salesManager: salesManagers
      .filter((manager) => manager.id || manager.mongoUserId)
      .map((manager) => ({
        value: manager.mongoUserId || manager.id,
        label: `${manager.name}${manager.isFormer ? ` (${t('filters.former', 'Former')})` : ''}`,
      })),
  }), [salesManagers, t]);

  // Handlers
  const handleSort = (key: QuotesFiltersType['sortKey']) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 1 ? -1 : 1);
    } else {
      setSortKey(key);
      setSortDirection(-1); // Default to descending for new sort key
    }
    setPage(0);
  };

  const handleStatusChange = (statuses: QuoteStatus[]) => {
    setSelectedStatuses(statuses);
    setPage(0);
  };

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    setPage(0);
  };

  const handleSelectedAccountChange = (accountId: string) => {
    setSelectedAccountId(accountId);
    setPage(0);
  };

  const loadExportQuotes = async () => {
    // The quotes endpoint caps pageLength at 100, so request the max allowed and let the
    // pagination loop below walk every page. Anything > 100 is rejected with a 400 and the
    // whole export fails (QUAL-5855).
    const pageLength = 100;
    const exportFilters: QuotesFiltersType = {
      ...filters,
      pageNumber: 0,
      pageLength,
    };

    const firstPage = await getQuotes(exportFilters);
    const allQuotes = [...(firstPage.quotes || [])];
    const expectedTotal = firstPage.total || allQuotes.length;

    let nextPage = 1;
    while (allQuotes.length < expectedTotal) {
      const response = await getQuotes({
        ...exportFilters,
        pageNumber: nextPage,
      });
      const nextQuotes = response.quotes || [];
      if (nextQuotes.length === 0) break;
      allQuotes.push(...nextQuotes);
      nextPage += 1;
    }

    return applyClientColumnFilters(allQuotes, columnFilters);
  };

  // CRUD handlers
  const handleCreateClick = () => {
    setEditingQuoteId(null);
    setEditingQuotePgId(null);
    setEditingQuoteContext(null);
    setDuplicateSourcePgId(null);
    createdQuotePgIdRef.current = null;
    setIsFormOpen(true);
  };

  const handleEditClick = (quote: Quote) => {
    setEditingQuoteId(quote.mongoQuoteId);
    setEditingQuotePgId(quote.id);
    setEditingQuoteContext(quote);
    setDuplicateSourcePgId(null);
    createdQuotePgIdRef.current = null;
    setIsFormOpen(true);
  };

  const handleDuplicateClick = (quote: Quote) => {
    // Open the form in create mode, pre-seeded from this quote (QuoteFormModal
    // duplicateFrom). No edit target → Save creates a brand-new quote.
    setEditingQuoteId(null);
    setEditingQuotePgId(null);
    setEditingQuoteContext(null);
    setDuplicateSourcePgId(quote.id);
    createdQuotePgIdRef.current = null;
    setIsFormOpen(true);
  };

  const handleDeleteClick = (quote: Quote) => {
    setDeletingQuote(quote);
  };

  const handleDiscardClick = (quote: Quote) => {
    setDiscardingQuote(quote);
  };

  const handleDiscardConfirm = () => {
    if (!discardingQuote) return;
    discardMutation.mutate(discardingQuote.id, { onSuccess: () => setDiscardingQuote(null) });
  };

  const handleFormSubmit = async (data: CreateQuoteInput | UpdateQuoteInput) => {
    // Route updates through the PG UUID so V4 read paths (Kanban, Print, lists)
    // reflect the change immediately. updateQuote() detects the UUID and PUTs
    // /v4/quotes/:id; no Mongo backing doc is required.
    //
    // `createdQuotePgIdRef` covers the "Save" (keep-open) create flow: once the
    // first save has created the quote, later saves must UPDATE it instead of
    // creating a duplicate.
    const targetPgId = editingQuotePgId || createdQuotePgIdRef.current;
    if (targetPgId) {
      await updateMutation.mutateAsync({ id: targetPgId, data: data as UpdateQuoteInput });
    } else {
      const created = await createMutation.mutateAsync(data as CreateQuoteInput);
      // Store the PG UUID (`id`), NOT `_id`: when the account has a legacy Mongo
      // org, create also dual-writes a Mongo shadow flagged `migrated_to_pg`, so
      // `_id` holds that shadow's ObjectId. Routing the keep-open follow-up save
      // by the ObjectId hits the V3 legacy passthrough and 423s ("managed in the
      // V2 app"); the PG UUID routes to the guard-free V4 PUT. Falling back to
      // `_id` covers accounts with no Mongo backing (there `_id` is the PG UUID).
      createdQuotePgIdRef.current = created?.id ?? created?._id ?? null;
      // Return the created quote so the form can attach sensors that were staged
      // before the quote (and its server-assigned number) existed.
      return created;
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingQuote) {
      toast.error(t('toast.cannotDelete'));
      return;
    }

    const pgQuoteId = deletingQuote.id;

    // No Mongo backing doc means there is nothing to delete in Mongo, so the bridge
    // is pure overhead: `deleteQuote` self-routes the PG UUID to the native V4 soft
    // delete. Materializing one first was also a dead end — on an account with no
    // legacy Mongo organisation `ensure-mongo` 409s, which aborted the delete and
    // left the quote permanently undeletable.
    if (!deletingQuote.mongoQuoteId) {
      deleteMutation.mutate({ pgQuoteId }, {
        onSuccess: () => {
          setDeletingQuote(null);
        },
      });
      return;
    }

    // The quote does have a Mongo doc, so delete through it to keep the legacy V1
    // app in step. The bridge still earns its round-trip here: it re-links a
    // dangling `mongoQuoteId` so we delete the live doc rather than a stale id.
    try {
      const ensured = await ensureMongoMutation.mutateAsync(pgQuoteId);
      deleteMutation.mutate({
        mongoQuoteId: ensured.mongoQuoteId,
        pgQuoteId,
      }, {
        onSuccess: () => {
          setDeletingQuote(null);
        },
      });
    } catch {
      // The ensure-mongo hook's own toast talks about editing, which is the wrong
      // verb here; name the action the user actually asked for.
      toast.error(t('toast.deleteFailed', 'Failed to delete quote'));
    }
  };

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <nav className="breadcrumb flex items-center gap-1.5">
        <Link to="/erp" className="text-atek-blue hover:text-atek-blue-dark">
          {t('page.erp')}
        </Link>
        <span className="text-muted-foreground">/</span>
        <span className="text-foreground">{t('page.title')}</span>
      </nav>

      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold text-foreground">{t('page.title')}</h1>
          <p className="text-[13px] text-muted-foreground mt-1">
            {t('page.subtitle')}
          </p>
        </div>
        {canCreate && (
          <Button onClick={handleCreateClick}>
            <Plus className="h-4 w-4 mr-1" />
            {t('page.newQuote')}
          </Button>
        )}
      </div>

      {/* Stats Bar */}
      {statsQuery.isLoading ? (
        <div className="grid gap-4 md:grid-cols-5">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : stats.length > 0 ? (
        <QuoteStatsBar stats={stats} isApproximate={isStatsApproximate} />
      ) : null}

      {/* Main Content Card */}
      <Card>
        <CardContent className="p-0">
          {quotesQuery.error ? (
            <div className="py-8 text-center">
              <p className="text-destructive">{t('errors.loadQuotes', 'Failed to load quotes. Please try again.')}</p>
            </div>
          ) : (
            <QuotesTable
              quotes={quotes}
              pageItemCount={rawQuotes.length}
              total={total}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={handleSort}
              onEdit={canEdit ? handleEditClick : undefined}
              onDelete={canDelete ? handleDeleteClick : undefined}
              onDuplicate={canCreate ? handleDuplicateClick : undefined}
              onDiscard={canDiscard ? handleDiscardClick : undefined}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              highlightId={highlightId || undefined}
              columnFilters={columnFilters}
              selectedAccountId={selectedAccountId}
              onSelectedAccountChange={handleSelectedAccountChange}
              onColumnFilterChange={setColumnFilter}
              onClearColumnFilters={() => {
                setColumnFilters({});
                setSelectedAccountId(ALL_ACCOUNTS_VALUE);
                setPage(0);
              }}
              onLoadExportQuotes={loadExportQuotes}
              isLoading={quotesQuery.isLoading}
              searchableSelectOptions={searchableSelectOptions}
              toolbarTitle={(
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold leading-5">{t('page.allQuotes')}</h2>
                  <p className="text-xs text-muted-foreground">
                    {t('page.quotesFound', { count: total })}
                  </p>
                </div>
              )}
              toolbarControls={(
                <QuotesFilters
                  selectedStatuses={selectedStatuses}
                  onStatusChange={handleStatusChange}
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                />
              )}
            />
          )}
        </CardContent>
      </Card>

      {/* Form Modal (Create/Edit) */}
      <QuoteFormModal
        open={isFormOpen && (
          duplicateSourcePgId
            ? (!duplicateSourceQuery.isLoading && !!duplicateSourceQuery.data)
            : (
              (!editingQuoteId && !editingQuotePgId)
              || (!editQuoteV4.isLoading && !!editQuoteV4.data)
            )
        )}
        onOpenChange={(open) => {
          if (!open) {
            setIsFormOpen(false);
            setEditingQuoteId(null);
            setEditingQuotePgId(null);
            setEditingQuoteContext(null);
            setDuplicateSourcePgId(null);
            createdQuotePgIdRef.current = null;
          }
        }}
        quote={editQuoteV3.data ?? null}
        v4Quote={editQuoteV4.data ?? null}
        duplicateFrom={duplicateSourcePgId ? (duplicateSourceQuery.data ?? null) : null}
        pgQuoteId={editingQuotePgId || undefined}
        ensuredMongoQuoteId={editingQuoteId || undefined}
        initialCustomerData={editingQuotePgId ? editingInitialCustomerData : undefined}
        onSubmit={handleFormSubmit}
        onCreateForNewCustomer={async (input) => {
          await createForNewCustomerMutation.mutateAsync(input);
        }}
        isLoading={createMutation.isPending || createForNewCustomerMutation.isPending || updateMutation.isPending || ensureMongoMutation.isPending}
      />

      {/* Delete Dialog */}
      <QuoteDeleteDialog
        open={!!deletingQuote}
        onOpenChange={(open) => {
          if (!open) setDeletingQuote(null);
        }}
        quote={deletingQuote}
        onConfirm={handleDeleteConfirm}
        isLoading={deleteMutation.isPending || ensureMongoMutation.isPending}
      />

      {/* Discard Dialog */}
      <QuoteDiscardDialog
        open={!!discardingQuote}
        onOpenChange={(open) => {
          if (!open) setDiscardingQuote(null);
        }}
        quote={discardingQuote}
        onConfirm={handleDiscardConfirm}
        isLoading={discardMutation.isPending}
      />
    </div>
  );
}
